import { auth } from "../firebase/config";

/**
 * Utility to call Firebase functions with proper authentication
 */
export class FirebaseFunctionProxy {
  private static aiContentFallbackUrl =
    "https://europe-west1-translatorx-5774e.cloudfunctions.net/generateAIContent";

  private static directFunctionUrls: Record<string, string | undefined> = {
    generateAIContent:
      process.env.REACT_APP_GENERATE_AI_CONTENT_URL ||
      FirebaseFunctionProxy.aiContentFallbackUrl,
  };

  /**
   * Base URL for Firebase functions
   * In production, this would be your deployed firebase functions URL
   * For local development, use the Firebase emulator
   */
  private static baseUrl =
    process.env.NODE_ENV === "production"
      ? "https://europe-west1-translation-project.cloudfunctions.net"
      : "http://localhost:5001/translation-project/europe-west1";

  /**
   * Call a Firebase function with authentication
   * @param functionName Name of the function to call
   * @param data Request data
   * @param method HTTP method
   * @returns Response from the function
   */
  static async callFunction(
    functionName: string,
    data: any = {},
    method: "GET" | "POST" = "POST"
  ): Promise<any> {
    try {
      // Wait for Firebase auth state restoration to avoid initial-load race
      await auth.authStateReady();

      // Get current user token
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("Authentication required. Please sign in.");
      }

      const idToken = await currentUser.getIdToken();

      // Prepare request options
      const requestOptions = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: method === "POST" ? JSON.stringify(data) : undefined,
      };

      // Build URL with query params for GET requests
      let url =
        this.directFunctionUrls[functionName]?.trim() ||
        `${this.baseUrl}/${functionName}`;

      if (method === "GET" && Object.keys(data).length > 0) {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
          params.append(key, String(value));
        });
        url = `${url}?${params.toString()}`;
      }

      // Call the function
      const response = await fetch(url, requestOptions);

      // Parse response
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            `Function call failed with status: ${response.status}`
        );
      }

      return responseData;
    } catch (error: any) {
      console.error(`Error calling Firebase function ${functionName}:`, error);
      throw error;
    }
  }
}

export default FirebaseFunctionProxy;
